import { defaultAllSelection } from "services/constants/selections";
export const ACCESS_CHANGED_EVENT = "organflow-access-changed";
export const notifyAccessChanged = () => window.dispatchEvent(new Event(ACCESS_CHANGED_EVENT));
// Asks the top bar to open its company picker with the "choose another
// company" highlight (from the workspace-unavailable dialog).
export const OPEN_COMPANY_PICKER_EVENT = "organflow-open-company-picker";
export const requestCompanyPicker = () => window.dispatchEvent(new Event(OPEN_COMPANY_PICKER_EVENT));

// A provisioning node's (product, module or submodule) access. The backend
// sends has_access at every level; a payload without it falls back to the
// old rule: viewer on the submodule, or on any submodule of a module.
export const nodeHasAccess = (node) => {
  if (typeof node?.has_access === "boolean") return node.has_access;
  if (Array.isArray(node?.submodules)) return node.submodules.some((sub) => sub?.permissions?.viewer === true);
  return node?.permissions?.viewer === true;
};

const pathOf = (value) => String(value || "").split("?")[0];

// The provisioning module behind a screen: the sidebar row (routes.js
// collapse entry, its permissionName or name) under productName whose path
// is this page or a parent of it. Null for a page with no sidebar row.
export const routeModuleName = (routes, pathname, productName) => {
  if (!Array.isArray(routes) || !productName) return null;
  let match = null;
  routes.filter((route) => route?.name === productName).forEach((route) =>
    (route.collapse || []).forEach((row) => {
      const rowPath = pathOf(row?.path);
      // Hidden rows (e.g. EOD Report) aren't gated by the sidebar either, so
      // they fall back to the product's access.
      if (rowPath && !row?.hideInSidebar &&(pathname === rowPath || pathname.startsWith(rowPath + "/")) &&
        (!match || rowPath.length > match.path.length)) {
        // Rows with skipAccessCheck (no backend module yet, e.g. Payroll >
        // Statutory) follow the product's access, as in the sidebar.
        match = { path: rowPath, name: row.skipAccessCheck ? null : row.permissionName || row.name };
      }
    }));
  return match?.name ?? null;
};

// Whether the screen may open: its own module's has_access. A module missing
// from the payload means no access (the sidebar hides its row too); otherwise
// a company with only Team Workspace > Home kept opening Approvals after a
// company switch, through the product's has_access. Pages with no sidebar
// row (moduleName null) follow the product's access.
export const screenHasAccess = (access, moduleName) => {
  const modules = Array.isArray(access?.module_details) ? access.module_details : [];
  if (moduleName) {
    const module = modules.find((item) => item?.module_name === moduleName);
    return module ? nodeHasAccess(module) : false;
  }
  if (typeof access?.has_access === "boolean") return access.has_access;
  return modules.some(nodeHasAccess);
};

export const canUseCompanyContext = (companyId, userType, companies = []) =>
  companyId != null && String(companyId) !== "" &&
  (String(companyId) !== String(defaultAllSelection) || ["Super Admin", "Support Admin"].includes(userType) ||
    (Array.isArray(companies) && companies.some((company) => String(company?.company_id) === String(defaultAllSelection))));

export const provisioningModule = (companyId, productName) =>
  String(companyId) === String(defaultAllSelection) ? "All" : productName;

export const matchesAccessContext = (access, companyId, productName) =>
  access != null && !Array.isArray(access) &&
  access.company_id != null && String(access.company_id) === String(companyId) &&
  (!productName || access.product_name === productName);

export const scopedProvisioning = (data, companyId, productName) => {
  // All-company provisioning returns multiple products; only the current
  // product's permissions may enter its gate and sidebar state.
  if (String(companyId) === String(defaultAllSelection)) {
    const products = Array.isArray(data) ? data : data?.products;
    if (Array.isArray(products)) {
      data = !Array.isArray(data) && data.company_id != null && String(data.company_id) !== String(companyId)
        ? null : products.find((product) => product.product_name === productName);
    }
  }
  const valid = data && !Array.isArray(data) &&
    (data.company_id == null || String(data.company_id) === String(companyId)) &&
    (data.product_name == null || data.product_name === productName);
  return {
    product_name: productName,
    company_id: companyId,
    ...(valid ? data : {}),
    module_details: valid && Array.isArray(data.module_details) ? data.module_details : [],
  };
};

export const createLatestRequest = () => {
  let sequence = 0;
  return {
    start: () => ++sequence,
    isCurrent: (request) => request === sequence,
  };
};

// App bootstrap and the sidebar share ordering, so a slow startup request
// cannot restore older grants after a navigation or refresh completes.
export const provisioningRequestOrder = createLatestRequest();
export const productRequestOrder = createLatestRequest();

export const PROVISIONING_TTL_MS = 5 * 60 * 1000;
export const provisioningCacheKey = (domain, userId, companyId, productName) =>
  JSON.stringify([domain, String(userId), String(companyId), productName]);

export const createProvisioningCache = (now = Date.now) => {
  const entries = new Map();
  return {
    get: (key) => {
      const entry = entries.get(key);
      if (!entry || now() - entry.ts >= PROVISIONING_TTL_MS) {
        entries.delete(key);
        return undefined;
      }
      return entry.data;
    },
    set: (key, data) => entries.set(key, { data, ts: now() }),
    delete: (key) => entries.delete(key),
    clear: () => entries.clear(),
  };
};

export const loadCachedProvisioning = async ({
  cache, inFlight, requests, cacheKey, companyId, productName,
  currentAccess, load, apply, force = false, background = false,
}) => {
  const request = requests.start();
  if (force) {
    cache.delete(cacheKey);
    inFlight.delete(cacheKey);
  }
  const cached = !force && cache.get(cacheKey);
  if (cached) {
    apply(cached);
    return;
  }
  if (!background && !matchesAccessContext(currentAccess, companyId, productName)) apply([]);
  let pending = inFlight.get(cacheKey);
  if (!pending) {
    pending = load();
    inFlight.set(cacheKey, pending);
  }
  let res;
  try { res = await pending; } catch { res = null; }
  finally { if (inFlight.get(cacheKey) === pending) inFlight.delete(cacheKey); }
  if (!requests.isCurrent(request)) return;
  const access = scopedProvisioning(res?.ok ? res.responseDetails : null, companyId, productName);
  if (res?.ok) cache.set(cacheKey, access);
  apply(access);
};
